import isFn from '@utils/is-function';
import setReadonly from '@utils/set-readonly';
import ReactiveComponent from "@utils/reactive-component";

class State extends ReactiveComponent {
    constructor(definitions) {
        const {
            getters = {},
            state = {}
        } = definitions;

        const computed = Object.fromEntries(
            Object.entries(getters).map(([ prop, fn ]) => {
                return [
                    prop,
                    () => fn(this.state, this.getters)
                ];
            })
        );

        super({ computed, state });

        this.state = {};
        this.#defineState(state);

        this.getters = {};
        this.#defineGetters(getters);
    }

    get enumerable() {
        return false
    }

    #defineGetters(getters) {
        setReadonly(this, 'getters');

        Object.keys(getters).forEach(prop => {
            Object.defineProperty(this.getters, prop, {
                get: () => this[prop]
            });

            this[prop]; // trigger getter to initialize any watchers
        })
    }

    #defineState(state) {
        setReadonly(this, 'state');

        Object.keys(state).forEach(prop => {
            Object.defineProperty(this.state, prop, {
                get: () => this[prop],
                set: () => { throw new Error(`Mutation on "${prop}" property detected. Do not mutate state outside of a mutation handler.`); }
            });
        });
    }

    watch(prop, fn) {
        if(!prop || !isFn(fn)) {
            return null;
        }

        const bindedFn = (...args) => fn(this.state, this.getters,  ...args);
        
        if(this.hasOwnProperty(prop)) {
            return super.watch(prop, bindedFn);
        }
    }
}

export default class Store extends State {
    constructor(definitions) {
        const {
            actions = {},
            getters = {},
            mutations = {},
            state = {},
        } = definitions;

        super({ getters, state });

        this.#bindActions(actions);
        this.#defineActions();

        this.#bindMutations(mutations);
        this.#defineMutations();
    }

    #defineActions() {
        setReadonly(this, 'actions');
        Object.keys(this.actions).forEach(prop => setReadonly(this.actions, prop));
    }

    #bindActions(actions) {
        const proxy = new Proxy(this, {
            get(target, prop) {
                return target[prop];
            }
        });

        const context = {
            commit: this.commit.bind(proxy),
            dispatch: this.dispatch.bind(proxy),
            getters: this.getters,
            state: this.state
        };

        this.actions = Object.fromEntries(
            Object.entries(actions).map(([prop, fn]) => {
                return [
                    prop,
                    (...args) => fn(context, ...args)
                ];
            })
        );
    }

    #defineMutations() {
        setReadonly(this, 'mutations');
        Object.keys(this.mutations).forEach(prop => setReadonly(this.mutations, prop));
    }

    #bindMutations(mutations) {
        this.mutations = Object.fromEntries(
            Object.entries(mutations).map(([prop, fn]) => {
                return [
                    prop,
                    (...args) => fn(this, ...args)
                ];
            })
        );
    }

    commit(name, ...args) {
        if(this.mutations[name]) {
            this.mutations[name](...args);
        }
    }

    dispatch(name, ...args) {
        if(this.actions[name]) {
            this.actions[name](...args);
        }
    }
}
